import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { isEqual } from 'lodash';
import { fractionAggFunc } from 'ui/AgGridReact/aggFuncs';
import { CellClickedEvent, ColDef } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import {
    unpackLabelFromResponseData,
    manageUnknownCellValue,
} from 'helpers/formatHelper';
import { getDecimalPrecision } from 'helpers/chart/settings';
import { ChartSettingsT, PivotFieldsAggridT } from 'business/definitions';
import PivotGrid from 'components/PivotGrid/PivotGrid';
import { style } from 'components/PivotGrid/defaultStyle';
import ExportMenuContainer from 'components/ExportMenu/ExportMenuContainer';
import i18n from 'i18n';
import { useDebounce } from 'hooks/helpers';
import {
    getFiltersFromTCDNode,
    getFilterFromClick,
} from 'ui/AgGridReact/helpers';
import { camelize } from 'helpers/syntaxHelper';
import { useIsExploreUser } from 'hooks/client';

import { getDefaultPivotGridColumns } from './defaultSettings';
import { useBackendSyncedContext } from 'routes/Explore/components/Contexts/BackendSyncedContext/BackendSyncedContext';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import { useIsSupportUser } from 'hooks/user';
type Props = {
    data: ResponseDataT[];
    orderedColumns: ResponseColumnT[];
    applyChartSettings: (arg1: ChartSettingsT) => void;
    settings: ChartSettingsT;
    onClick: FlowAnyFunction;
    isChartViewedOnDashboard: boolean;
    hideSideBar?: boolean;
};

const DEFAULT_COL_DEF = {
    flex: 1,
    minWidth: 80,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    suppressFiltersToolPanel: true,
};

const StyPivotGrid = styled('div', {
    name: 'StyPivotGrid',
})({
    height: '100%',
    '& .Mui-custom-pivot-grid': {
        ...style,
        '& .ag-header-cell-text': {
            fontSize: '10px !important',
        },
        '& .ag-header-group-text': {
            fontSize: '10px !important',
        },
        '& .ag-group-value': {
            fontSize: '10px !important',
            fontWeight: '500',
        },
    },
});

const COLUMN_CHANGED_DEBOUNCE_DELAY = 1000; // ms, = 1 second

export const DecisionPivotGrid = ({
    data,
    orderedColumns,
    applyChartSettings,
    settings,
    onClick,
    isChartViewedOnDashboard,
    hideSideBar = false,
}: Props): React.ReactElement => {
    const isSupportUser = useIsSupportUser();
    const initialColDefs = getDefaultPivotGridColumns(
        orderedColumns,
        isSupportUser,
    );
    const isExploreUser = useIsExploreUser();
    const dashboard = useGetDashboardFromSlugOrMetadata();

    const canUserEditDashboard = dashboard?.canEdit || false;

    // We want to insure that the user cannot edit only if:
    // - the chart is viewed on a dashboard and he has edit rights on the dashboard
    // - the chart is not viewed on a dashboard
    const canEdit =
        (isChartViewedOnDashboard && canUserEditDashboard) ||
        !isChartViewedOnDashboard;

    const { isBackendSynced, setIsBackendSynced } = useBackendSyncedContext();
    const [isUserActing, setIsUserActing] = useState(false);

    const onColumnsChanged = (pivotFieldsAggrid: PivotFieldsAggridT) => {
        if (
            canEdit &&
            !isEqual(pivotFieldsAggrid, settings?.pivotFieldsAggrid)
        ) {
            applyChartSettings({
                ...settings,
                pivotFieldsAggrid,
            });
        }
    };

    const onModelUpdate = useCallback(() => {
        if (isBackendSynced && isUserActing) {
            setIsBackendSynced(false);
        }
    }, [isBackendSynced, setIsBackendSynced, isUserActing]);

    //hacky way to detect that user is interacting with the grid, moving columns
    const onDragStarted = useCallback(() => {
        if (!isUserActing) setIsUserActing(true);
    }, [setIsUserActing, isUserActing]);

    const onColumnsChangedDebounced = useDebounce(
        onColumnsChanged,
        COLUMN_CHANGED_DEBOUNCE_DELAY,
    );

    const [ExporterToExcel, setExporterToExcel] = React.useState<
        // @ts-expect-error [TS migration] Was not detected by flow
        null | ((Function) => void)
    >(null);

    // this is what allows us to display timestamp on Pivot Value Header
    // see docs here: https://www.ag-grid.com/react-grid/pivoting/#manipulating-secondary-columns
    const processPivotResultColGroupDef = (colGroupDef: ColDef) => {
        function isNumeric(n: string) {
            return !Number.isNaN(parseFloat(n)) && Number.isFinite(Number(n));
        }

        // his is a hack which relies on the fact that except dates, no other dimensions will have integer values between Date(2000-1-1) and Date(2050-1-1)
        function isValidTimestamp(timestamp: string | undefined) {
            if (!timestamp) return false;
            const dateToCheck = new Date(parseFloat(timestamp)).getTime();
            return (
                isNumeric(timestamp) &&
                new Date('2000-1-1').valueOf() < dateToCheck &&
                dateToCheck < new Date('2050-1-1').valueOf()
            );
        }

        // Header Name is the pivot Value Header displayed, as timestamp are not converted by the value formatter
        // we need to reformat them here.
        if (isValidTimestamp(colGroupDef.headerName)) {
            const timestamp = parseInt(colGroupDef.headerName!, 10);
            colGroupDef.headerName = new Date(timestamp).toLocaleDateString(
                i18n.language,
            );
        }
    };

    const onCellClicked = useCallback(
        ({ node, colDef, api }: CellClickedEvent) => {
            // Get all filters from the clicked node
            const allFiltersFromNode = getFiltersFromTCDNode(
                colDef.pivotKeys || [],
                node,
                api,
            );

            // Filter the allFiltersFromNode list to get only the filters from the deployed columns
            const clickedColumn = colDef.colId!;
            const filters = getFilterFromClick(
                clickedColumn,
                api,
                allFiltersFromNode,
            );
            // Parse the filters to a point
            const point = {};
            filters.forEach((filter) => {
                // @ts-expect-error [TS migration] Was not detected by flow
                point[camelize(filter.field)] = filter.value;
            });
            onClick(point);
        },
        [],
    );

    const gridProps: Partial<AgGridReactProps> = {
        sideBar: {},
        defaultColDef: DEFAULT_COL_DEF,
        pivotDefaultExpanded: -1, // -1 means all pivot groups are expanded by default
        processPivotResultColGroupDef,
        // we need this context to allow conditional formatting on currencies
        // see doc here: https://www.ag-grid.com/react-data-grid/context/
        context: {
            decimalPrecision: getDecimalPrecision(settings),
            displayBy: settings.displayBy,
        },
        aggFuncs: { fractionAggFunc },
        onCellClicked,
        onDragStarted,
    };

    if (isExploreUser && canEdit && !hideSideBar) {
        gridProps.sideBar = {
            toolPanels: [
                {
                    id: 'columns',
                    labelKey: 'columns',
                    labelDefault: 'Columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressPivotMode: true,
                    },
                },
            ],
        };
    }

    if (!canEdit) {
        gridProps.defaultColDef = {
            ...gridProps.defaultColDef,
            menuTabs: [],
        };
    }
    return (
        <>
            <ExportMenuContainer
                className="pivot-grid-export-container"
                exportExcelData={ExporterToExcel}
            />
            <StyPivotGrid>
                <PivotGrid
                    className="Mui-custom-pivot-grid"
                    data={data
                        .map(unpackLabelFromResponseData)
                        .map((row) => manageUnknownCellValue(row, '-'))}
                    initialColDefs={initialColDefs}
                    pivotFieldsAggrid={settings.pivotFieldsAggrid}
                    setExporterToExcel={setExporterToExcel}
                    onColumnsChanged={onColumnsChangedDebounced}
                    gridProps={gridProps}
                    groupIncludeFooter={settings.showGroupAggregates}
                    groupIncludeTotalFooter={settings.showAggregates}
                    onModelUpdate={onModelUpdate}
                />
            </StyPivotGrid>
        </>
    );
};
